/**
 * @file Projectile component (GDD §8).
 *
 * A bullet carries its own damage and range so the weapon that fired it can be
 * destroyed, swapped or re-tuned without affecting shots already in flight.
 */

import { Component } from '../ecs/Component.js';

export class Projectile extends Component {
  /**
   * @param {object} options Projectile setup.
   * @param {number} options.damage Damage dealt on hit.
   * @param {number} options.range Maximum travel distance in world units.
   * @param {number} [options.ownerId] Entity id of the shooter, so it cannot hit itself.
   * @param {number} [options.pierce] How many targets it may pass through.
   * @param {import('../combat/Shot.js').ShotRecord | null} [options.shot] Ledger
   *   shared with every other projectile of the same trigger pull.
   * @param {number} [options.falloffStart] Fraction of the range where damage starts falling.
   * @param {number} [options.falloffMin] Damage multiplier at maximum range.
   * @param {number} [options.knockback] Multiplier on the shove the impact delivers.
   */
  constructor({
    damage,
    range,
    ownerId = 0,
    pierce = 0,
    shot = null,
    falloffStart = 1,
    falloffMin = 1,
    knockback = 1
  }) {
    super();
    /** @type {number} */
    this.damage = damage;
    /** @type {number} */
    this.range = range;
    /** @type {number} Distance travelled so far. */
    this.travelled = 0;
    /** @type {number} */
    this.ownerId = ownerId;
    /** @type {number} Remaining targets it may pass through before expiring. */
    this.pierce = pierce;
    /**
     * Shared with the other projectiles of the same shot, which is what stops a
     * shotgun blast from dealing nine times its damage to one body.
     * @type {import('../combat/Shot.js').ShotRecord | null}
     */
    this.shot = shot;
    /** @type {number} Fraction of the range where damage starts falling, 0..1. */
    this.falloffStart = falloffStart;
    /** @type {number} Damage multiplier at maximum range, 0..1. */
    this.falloffMin = falloffMin;
    /**
     * Carried by the round rather than read back off the weapon, for the same
     * reason damage is: the weapon may be gone by the time this lands.
     * @type {number}
     */
    this.knockback = knockback;
    /**
     * Entity ids already damaged by this round. A piercing shot that ends a
     * frame still overlapping a large target would otherwise re-hit it on the
     * next sweep and spend pierce (and damage) again.
     * @type {number[]}
     */
    this.hitIds = [];
  }

  /** @returns {void} */
  reset() {
    this.travelled = 0;
    this.pierce = 0;
    this.shot = null;
    this.falloffStart = 1;
    this.falloffMin = 1;
    this.knockback = 1;
    this.hitIds.length = 0;
  }
}
